import { Product } from '@@types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { ReactSVG } from 'react-svg'

import { useFetchProduct } from '@entities/Product'

import { useAuthFlow } from '@features/auth'
import ControlCart from '@features/cart/ui/control/CartControl'
import { useFavoriteCount } from '@features/favorite/hooks/useFavoriteCount'
import { useAddressStore } from '@features/shipping-methods'

import { URL_CUSTOMER_FAVORITES, URL_CUSTOMER_PROFILE } from '@shared/contants'
import { LocalizedLink as Link, useLocalizedNavigate as useNavigate } from '@shared/lib/locale'

import styles from './styles.module.scss'

type TabButtonProps = {
  title: string;
  icon: string;
  counter?: number | string | null;
  href?: string;
  isAddingCart?: boolean;
  price?: string | number;
  countText?: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement> &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

const TabButton = ({
  title,
  icon,
  counter,
  href,
  isAddingCart,
  price,
  countText,
  ...props
}: TabButtonProps) => {
  const content = (
    <>
      <span className={styles.tabbar__button_icon}>
        <ReactSVG src={`/icons/${icon}.svg`} />
      </span>
      {!isAddingCart && (
        <span className={styles.tabbar__button_title}>{title}</span>
      )}
      {isAddingCart && (
        <span className={styles.tabbar__cart_block}>
          <span className={styles.tabbar__price}>{price}</span>
          <span className={styles.tabbar__productsCount}>{countText}</span>
        </span>
      )}
      {counter != null && counter !== 0 && counter !== '0' && (
        <span className={styles.counter}>{counter}</span>
      )}
    </>
  )

  const className = classNames(styles.tabbar__button, {
    [styles.addingCart]: isAddingCart,
  })

  if (href) {
    // Если href есть, рендерим Link
    return (
      <Link to={href} className={className} {...props}>
        {content}
      </Link>
    )
  }
  else {
    // Если нет href, то рендерим кнопку
    return (
      <button
        type="button"
        className={classNames('reset-button', className)}
        {...props}
      >
        {content}
      </button>
    )
  }
}

type TabBarProps = {
  showCartButton?: boolean;
  withProductQuery?: boolean;
};

export const TabBar = ({
  showCartButton = true,
  withProductQuery = false,
}: TabBarProps) => {
  const { t: tn } = useTranslation('navigation')
  const { t: tc } = useTranslation('common')

  const { slug } = useParams()
  const { shopId } = useAddressStore()
  const favoriteCount = useFavoriteCount(shopId)

  const navigate = useNavigate()
  const authFlow = useAuthFlow(() => navigate(`${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_FAVORITES}`))

  const { data: { product } = {} } = useFetchProduct({
    shopId: shopId,
    slug,
    previewStyle2: '800xauto',
  }
  )

  return (
    <nav className={styles.tabbar}>
      <div className={styles.tabbar__bg}></div>

      <div className={styles.tabbar__row}>
        <TabButton
          href="/catalog"
          key="tab-1"
          title={tn('catalog')}
          icon="grid-black"
        />

        {withProductQuery && !!product && (
          <ControlCart
            isTablet={false}
            product={product as Product}
            isInTabBar={true}
          />
        )}

        {showCartButton && !withProductQuery && (
          <TabButton
            href="/cart"
            key="tab-2"
            title={tc('cart.button')}
            icon="shopping-bag"
            isAddingCart={false}
          />
        )}
        <TabButton
          key="tab-3"
          title={tn('favorites')}
          icon="heart"
          counter={favoriteCount}
          onClick={authFlow}
        />
      </div>
    </nav>
  )
}
